<template>

    <v-card class="d-flex flex-column">

        <material-header title="Calendar view" info="Scheduled projects" icon="mdi-calendar-today">

            <template slot="buttons">

                <v-tooltip color="primary" left>
                    <template v-slot:activator="{ on }">
                        <v-btn :disabled="!hasPermission('view projects')"
                               @click="closeCalendar"
                               v-on="on"
                               color="red"
                               small
                               fab>
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>
                    <span>Close</span>
                </v-tooltip>

            </template>
        </material-header>

        <!-- HEADER DATE -->
        <v-card flat class="flex-shrink-1">
            <v-card-text class="pt-0 pb-0">
                <v-container fluid class="justify-space-between pt-0 pl-0 pr-0">
                    <v-row dense>
                        <v-col>

                            <span v-if="modeModel==='month'" class="display-1">{{currentMonth}}
                                <span class="grey--text"> | {{currentYear}} </span>
                            </span>

                            <span v-if="modeModel==='week'" class="display-1">
                                Week # {{currentWeek}}
                                <span class="grey--text"> | {{currentMonth}} {{currentYear}}</span>
                            </span>

                            <span v-if="modeModel==='day'" class="display-1">{{currentMonth}}
                                <span class="grey--text"> | {{currentYear}}</span>
                            </span>

                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
        </v-card>

        <!-- BUTTONS -->
        <v-card flat>

            <v-container fluid class="justify-space-between pt-0 pb-0">
                <v-row>
                    <v-col sm4 xs12>
                        <v-btn @click="$refs.calendar.prev()" color="primary">
                            <v-icon left dark>mdi-arrow-left-bold</v-icon>
                            Prev

                        </v-btn>
                    </v-col>
                    <!--
                                        <v-col class="text-center">
                                            <v-btn-toggle v-model="modeModel" dense mandatory rounded style="background-color: #555">
                                                <v-btn text value="month">
                                                    Month
                                                </v-btn>
                                                <v-btn text value="week">
                                                    Week
                                                </v-btn>
                                            </v-btn-toggle>
                                        </v-col>
                    -->
                    <v-col sm4 xs12 class="text-right">
                        <v-btn @click="$refs.calendar.next()" color="primary">
                            Next
                            <v-icon right dark>mdi-arrow-right-bold</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-container>

        </v-card>

        <!-- EVENT MENU -->
        <v-menu v-model="eventMenuModel"
                offset-x
                :position-x="eventPositionX"
                :position-y="eventPositionY"
                min-width="450px"
        >
            <v-card flat>
                <v-card-text primary-title class="pt-0">
                    <project-details-card @edit="editProject"
                                          :project="this.eventProject"
                    ></project-details-card>
                </v-card-text>
            </v-card>
        </v-menu>

        <!-- CALENDAR -->
        <v-calendar
                style="width: calc(100% - 30px)"
                class="flex-grow-1 ml-4 mr-4 mb-4"
                ref="calendar"
                v-model="calendarModel"
                :type="modeModel"
                :events="events"
                :event-color="getEventColor"
                color="primary"
                @change="getEvents"
                @click:date="dayNumberClicked"
                @click:event="eventClicked">
        </v-calendar>

        <!-- SHOW EVENT DIALOG -->
        <v-dialog v-model="dayEventsDialogModel" max-width="400">
            <v-toolbar dense color="primary" dark>
                <v-btn icon>
                    <v-icon>mdi-calendar</v-icon>
                </v-btn>
                <v-toolbar-title>All events</v-toolbar-title>

            </v-toolbar>

            <day-events-card :data="dayEventsData"
            ></day-events-card>
        </v-dialog>

        <json v-if="debug" :data="eventProject"></json>
    </v-card>

</template>

<script>

    import Json from 'vue-json-pretty';
    import Moment from 'moment';
    import {extendMoment} from 'moment-range';
    import {v4 as uuid} from 'uuid';
    import MaterialHeader from '../../../components/UI/MaterialHeader';
    import ProjectDetailsCard from './ProjectDetailsCard';
    import DayEventsCard from './DayEventsCard';

    const moment = extendMoment(Moment);

    export default {

        components: {
            Json,
            MaterialHeader,
            ProjectDetailsCard,
            DayEventsCard
        },

        props: {
            projects: {
                type: Array,
                required: true,
            }
        },

        data() {
            return {
                debug: false,
                events: [],
                modeModel: 'month',
                today: moment().format('YYYY-MM-DD HH:mm:ss'),
                calendarModel: moment().format('YYYY-MM-DD H:m:ss'),
                lastMap: null,
                eventColors: [
                    '#417e43',
                    '#3e6fa4',
                    '#8a663d',
                    '#918931', // lime darken-4
                    '#9b5e6b',
                    '#777777',
                    '#0D47A1', // blue darken-4
                    '#006064', // cyan darken-4
                    '#1B5E20', // green darken-4
                    '#408498', // blue-grey darken-3
                    '#4E342E', // brown-darken-4
                ],
                eventColorPointer: 0,
                dayEventsDialogModel: false,
                dayEventsData: [],
                eventMenuModel: false,
                eventProject: null,
                eventPositionX: null,
                eventPositionY: null,
            }
        },

        computed: {
            currentMonth() {
                return moment(this.calendarModel).format('MMMM');
            },

            currentWeek() {
                return moment(this.calendarModel).format('w');
            },

            currentYear() {
                return moment(this.calendarModel).format('YYYY');
            }
        },

        methods: {

            // convert the list of events into a map of lists keyed by date
            getEvents({start, end}) {
                const events = []

                // Reset pointer so that each project color stays the same thru page navigations
                this.eventColorPointer = 0;

                this.projects.forEach((project) => {

                    if (project.status === 'online') {

                        const format = this.modeModel === 'week' ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD';

                        events.push({
                            name: project.name,
                            start: moment.utc(project.start_date).local().startOf('day').format(format),
                            end: moment.utc(project.end_date).local().endOf('day').format(format),
                            color: this.allocateColor(),
                            //project: project,
                            uuid: uuid(),
                            project: project,
                        });

                    }
                });

                this.events = events;
            },

            eventClicked(data) {
                this.eventPositionX = data.nativeEvent.clientX;
                this.eventPositionY = data.nativeEvent.clientY;
                this.eventProject = data.event.project;
                this.eventMenuModel = true;
            },

            showEvent(event, date) {
                return moment(date).isBetween(event.start, event.end);
            },

            getEventColor(event) {
                return event.color
            },

            allocateColor() {
                if (!this.eventColors[this.eventColorPointer]) this.eventColorPointer = 0;
                const color = this.eventColors[this.eventColorPointer];
                this.eventColorPointer++;
                return color;
            },

            getDayEvents(date) {
                return this.lastMap[date];
            },

            dayNumberClicked(dayObject) {
                this.dayEventsData = this.getDayEvents(dayObject.date);
                this.dayEventsDialogModel = true;
            },

            closeCalendar() {
                this.$emit('close');
            },

            editProject(project) {
                this.$emit('edit', project);
            }
        }
    }
</script>

<style scoped>

    .calendar-base {
        height: 100%;
        border: 1px solid yellow;
    }

    .my-event {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        border-radius: 2px;
        background-color: #37474F;
        color: #ffffff;
        font-size: 12px;
        padding: 3px;
        cursor: pointer;
        margin: 1px 2px 1px 2px;
    }

</style>
